import React, { useEffect, useState } from 'react'
import { View, ViewStyle } from 'react-native'
import { useSelector } from 'react-redux'

import { addDays, format } from 'date-fns'
import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Capacity, ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AlertIconStroke } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AlertIconStroke'
import { LoadingSkeleton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/loadingSkeleton/LoadingSkeleton'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './availabilityCard.module.scss'
import CapacitiesFull from './CapacitiesFull'
import CapacitiesMinified from './CapacitiesMinified'
import CaseloadCapacities from './CaseloadCapacities'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getAuthConfig, getAuthUser } from '../../../data/auth/authSelectors'
import { getRequestPaymentSelectedProvider } from '../../../data/requestPayment/requestPaymentSelectors'
import { useCapacityUpdateFlow } from '../../../hooks/useCapacityUpdateFlow'
import { BCC_ROLES, BCT_ROLES, ROLES } from '../../constants/appConstants'
import { getTotalCapacity, hasRole } from '../../utils/utils'

const CalendarOpenSlotsContainer = styled.View({
  display: 'flex',
})

const RowContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
})

const CalendarOpenSlotsLoadingContainer = styled.View(({ theme }) => ({
  marginRight: theme.spacing['16px'],
  width: '220px',
  height: '50px',
}))

const CalendarOpenSlots = styled.View(({ theme }) => ({
  alignItems: 'center',
  borderRightWidth: '1px',
  borderColor: theme.colors.borderDefault,
  display: 'flex',
  flexDirection: 'row',
  marginRight: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
}))

const CalendarAvailabilitiesBlock = styled.View(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundSection,
  borderRadius: '5px',
  display: 'flex',
  fontSize: '34px',
  fontWeight: 600,
  height: '44px',
  justifyContent: 'center',
  paddingLeft: theme.spacing['4px'],
  paddingRight: theme.spacing['4px'],
  minWidth: '44px',
  width: 'fit-content',
}))

const CalendarAvailabilitiesTextContainer = styled.View(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: theme.spacing['8px'],
  marginRight: theme.spacing['8px'],
}))

const CalendarAvailabilitiesTitle = styled(BodyText)(({ theme }) => ({
  color: theme.colors.textPrimary,
  marginLeft: '0px',
}))

const CalendarAvailabilitiesDateRange = styled.View({
  paddingLeft: '0px',
})

const LowCalendarAvailabilitiesAlertContainer = styled.View(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundWarning,
  borderRadius: theme.spacing['4px'],
  color: theme.colors.textWarning,
  display: 'flex',
  flexDirection: 'row',
  height: '26px',
  padding: `${theme.spacing['4px']} ${theme.spacing['8px']}`,
}))

const LowAvailabilityTextContainer = styled.View(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 700,
  marginLeft: theme.spacing['4px'],
  textTransform: 'uppercase',
}))

export const AvailabilityCard: React.FC<AvailablityCardProps> = ({
  isMinified = false,
  caseloadDashboardCapacity = false,
  hideCalendarAvailability = false,
  hideProgramIcon = false,
  containerStyles,
}) => {
  const { colors } = useTheme()
  const selectedProvider = useSelector(getRequestPaymentSelectedProvider)
  const user = useSelector(getAuthUser)
  const config = useSelector(getAuthConfig)
  const provider = selectedProvider || user
  const {
    currentCapacity,
    providerAvailabilities,
    isLoadingAvailability: isLoading,
    isStale: isStaleAvailability,
    isCapacityProvider,
    openUpdateFlow,
    modals,
  } = useCapacityUpdateFlow()
  const { showCalendarAvailabilitiesWithCapacity } = useFlags()
  const [shouldRequestAvailability, toggleRequestProviderAvailability] = useState(isStaleAvailability)
  const shouldSeeNewAvailabilityCard = config?.bcPlatform ?? false
  const hasProperRolesToSeeCalendarAvailabilities = hasRole(provider?.roles, [...BCC_ROLES, ...BCT_ROLES])

  useEffect(() => {
    toggleRequestProviderAvailability(isStaleAvailability)
  }, [isStaleAvailability])
  useEffect(() => {
    if (isCapacityProvider) {
      track({ event: 'SHOW_PROVIDER_CAPACITY' })
    }
  }, [isCapacityProvider])
  const providerName = selectedProvider ? `${provider?.first_name} ${provider?.last_name}` : `you`
  const currentAvailabilityStatusText = (program: string) => {
    const capacity = currentCapacity[program as keyof Capacity]
    return isCapacityProvider ? (
      capacity ? (
        capacity === 1 ? (
          `Accepting 1 new Lyra client`
        ) : (
          `Accepting up to ${capacity} new Lyra clients`
        )
      ) : (
        `NOT accepting new Lyra clients`
      )
    ) : (
      <>
        New clients see {providerName} as <b>{provider?.available ? 'available' : 'not available'}</b> to book on Lyra
      </>
    )
  }

  const handleUpdateClick = () => {
    if (isCapacityProvider) {
      openUpdateFlow()
    } else {
      toggleRequestProviderAvailability(true)
      track({ event: 'BUTTON_PRESS', action: 'UPDATE_AVAILABILITY' })
    }
  }

  if (isCapacityProvider && !isEmpty(currentCapacity)) {
    return (
      <View testID={tID('AvailabilityCard-container')} style={containerStyles}>
        {modals}
        {showCalendarAvailabilitiesWithCapacity && shouldSeeNewAvailabilityCard ? (
          <RowContainer testID={tID('AvailabilityCard-rowContainer')}>
            {shouldSeeNewAvailabilityCard && !hideCalendarAvailability && hasProperRolesToSeeCalendarAvailabilities && (
              <CalendarOpenSlotsContainer>
                {isLoading ? (
                  <CalendarOpenSlotsLoadingContainer>
                    <LoadingSkeleton width='100%' height={50} />
                  </CalendarOpenSlotsLoadingContainer>
                ) : (
                  <CalendarOpenSlots testID={tID('AvailabilityCard-calendar-open-slots-container')}>
                    <CalendarAvailabilitiesBlock>{providerAvailabilities?.length ?? 0}</CalendarAvailabilitiesBlock>
                    <CalendarAvailabilitiesTextContainer>
                      <CalendarAvailabilitiesTitle>Calendar Availability</CalendarAvailabilitiesTitle>
                      <CalendarAvailabilitiesDateRange>
                        <BodyText
                          color={colors.textSecondary}
                          text={`${format(new Date(), 'M/d')}-${format(addDays(new Date(), 14), 'M/d')}`}
                          size={BodyTextSize.SMALL}
                        />
                      </CalendarAvailabilitiesDateRange>
                    </CalendarAvailabilitiesTextContainer>
                    {!hasRole(provider?.roles, [ROLES.LT_PRESCRIBER]) &&
                      getTotalCapacity(currentCapacity) > providerAvailabilities?.length && (
                        <LowCalendarAvailabilitiesAlertContainer
                          testID={tID('AvailabilityCard-low-availability-alert')}
                        >
                          <AlertIconStroke />
                          <LowAvailabilityTextContainer>
                            <BodyText text='Low' color={colors.textWarning} size={BodyTextSize.CAPTION} />
                          </LowAvailabilityTextContainer>
                        </LowCalendarAvailabilitiesAlertContainer>
                      )}
                  </CalendarOpenSlots>
                )}
              </CalendarOpenSlotsContainer>
            )}
            {caseloadDashboardCapacity ? (
              <div className={styles['caseload-capacities-container']}>
                <CaseloadCapacities
                  provider={provider as ProviderUser}
                  currentAvailabilityStatusText={(programName: string) => currentAvailabilityStatusText(programName)}
                  handleUpdateClick={handleUpdateClick}
                  currentCapacity={currentCapacity}
                />
              </div>
            ) : (
              <>
                {isMinified ? (
                  <div className={styles['minified-container']}>
                    <CapacitiesMinified
                      provider={provider as ProviderUser}
                      currentAvailabilityStatusText={(programName: string) =>
                        currentAvailabilityStatusText(programName)
                      }
                      handleUpdateClick={handleUpdateClick}
                      currentCapacity={currentCapacity}
                      hideProgramIcon={hideProgramIcon}
                    />
                  </div>
                ) : (
                  <CapacitiesFull
                    provider={provider as ProviderUser}
                    selectedProvider={selectedProvider as unknown as ProviderUser}
                    currentAvailabilityStatusText={currentAvailabilityStatusText}
                    handleUpdateClick={handleUpdateClick}
                    shouldRequestAvailability={shouldRequestAvailability}
                    isCapacityProvider={isCapacityProvider}
                  />
                )}
              </>
            )}
          </RowContainer>
        ) : caseloadDashboardCapacity ? (
          <div className={styles['caseload-capacities-container']}>
            <CaseloadCapacities
              provider={provider as ProviderUser}
              currentAvailabilityStatusText={(programName: string) => currentAvailabilityStatusText(programName)}
              handleUpdateClick={handleUpdateClick}
              currentCapacity={currentCapacity}
            />
          </div>
        ) : (
          <>
            {isMinified ? (
              <div className={styles['minified-container']}>
                <CapacitiesMinified
                  provider={provider as ProviderUser}
                  currentAvailabilityStatusText={(programName: string) => currentAvailabilityStatusText(programName)}
                  handleUpdateClick={handleUpdateClick}
                  currentCapacity={currentCapacity}
                  hideProgramIcon={hideProgramIcon}
                />
              </div>
            ) : (
              <CapacitiesFull
                provider={provider as ProviderUser}
                selectedProvider={selectedProvider as unknown as ProviderUser}
                currentAvailabilityStatusText={currentAvailabilityStatusText}
                handleUpdateClick={handleUpdateClick}
                shouldRequestAvailability={shouldRequestAvailability}
                isCapacityProvider={isCapacityProvider}
              />
            )}
          </>
        )}
      </View>
    )
  } else {
    return null
  }
}

type AvailablityCardProps = {
  isMinified?: boolean
  caseloadDashboardCapacity?: boolean
  hideCalendarAvailability?: boolean
  hideProgramIcon?: boolean
  containerStyles?: ViewStyle
}

export default AvailabilityCard
